import React, { useState, useEffect, useCallback, useRef } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TablePagination from "@mui/material/TablePagination";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Snackbar from "@mui/material/Snackbar";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
// import { useAuth } from "../contexts/AuthContext";
import { apiCall } from "../config/api";
import { db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  addDoc,
  GeoPoint,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { visuallyHidden } from "@mui/utils";

// Tarih formatlama fonksiyonu (ISO string veya Date)
const formatDate = (value) => {
  if (!value) return "N/A";

  // Backend artık ISO string gönderiyor
  let date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string") {
    date = new Date(value);
  } else if (value.toDate) {
    // Fallback: Firestore Timestamp
    date = value.toDate();
  } else {
    return "N/A";
  }

  // Invalid Date kontrolü
  if (isNaN(date.getTime())) return "N/A";

  return date.toLocaleDateString("tr-TR");
};

// Sıralama için yardımcı fonksiyonlar
function descendingComparator(a, b, orderBy) {
  let valA = a[orderBy];
  let valB = b[orderBy];

  // Handle special cases
  if (
    orderBy === "createdAt" ||
    orderBy === "deletedAt" ||
    orderBy === "undoDeadline"
  ) {
    // createdAt is already a Date object from fetchPins
    valA = a[orderBy] instanceof Date ? a[orderBy] : null;
    valB = b[orderBy] instanceof Date ? b[orderBy] : null;

    // Handle null dates (put them at the end)
    if (!valA && !valB) return 0;
    if (!valA) return 1;
    if (!valB) return -1;

    return valB - valA; // Descending: newer first
  }

  if (valA === undefined || valA === null) valA = "";
  if (valB === undefined || valB === null) valB = "";

  // Numeric comparison
  if (typeof valA === "number" && typeof valB === "number") {
    return valB - valA;
  }

  // String comparison (case insensitive)
  if (typeof valA === "string") valA = valA.toLowerCase();
  if (typeof valB === "string") valB = valB.toLowerCase();

  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const pinTierOptions = ["Free", "Basic", "Premium", "Standard"];
const pinStatusOptions = ["Aktif", "Pasif"];
// Backend status mapping: backend gönderiyor -> frontend gösteriyor
const statusMapping = {
  active: "Aktif",
  Aktif: "Aktif",
  inactive: "Pasif",
  Pasif: "Pasif",
};
const pinTypeOptions = [
  { value: "all", label: "Tümü" },
  { value: "audio", label: "Sesli" },
  { value: "text", label: "Metin" },
];

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Tablo başlıkları
const headCells = [
  { id: "title", numeric: false, disablePadding: true, label: "Başlık" },
  {
    id: "description",
    numeric: false,
    disablePadding: false,
    label: "Açıklama",
  },
  {
    id: "imageUrl",
    numeric: false,
    disablePadding: false,
    label: "Resim Pini",
    sortable: false,
  },
  { id: "id", numeric: false, disablePadding: false, label: "Pin ID" },
  { id: "pinType", numeric: false, disablePadding: false, label: "Pin Türü" },
  {
    id: "category",
    numeric: false,
    disablePadding: false,
    label: "Kategori",
    sortable: false,
  },
  {
    id: "pinTier",
    numeric: false,
    disablePadding: false,
    label: "Pin Seviyesi",
  },
  { id: "status", numeric: false, disablePadding: false, label: "Durumu" },
  {
    id: "isPromoted",
    numeric: false,
    disablePadding: false,
    label: "Reklam Pini",
    sortable: false,
  },
  {
    id: "isQuarantined",
    numeric: false,
    disablePadding: false,
    label: "Karantina Durumu",
    sortable: false,
  },
  {
    id: "createdAt",
    numeric: false,
    disablePadding: false,
    label: "Oluşturulma Tarihi",
  },
  {
    id: "moderationStatus",
    numeric: false,
    disablePadding: false,
    label: "Denetim Durumu",
    sortable: true,
  },
  { id: "latitude", numeric: true, disablePadding: false, label: "Enlem" },
  { id: "longitude", numeric: true, disablePadding: false, label: "Boylam" },
  { id: "city", numeric: false, disablePadding: false, label: "Şehir" },
  { id: "country", numeric: false, disablePadding: false, label: "Ülke" },
  {
    id: "pinOwnerName",
    numeric: false,
    disablePadding: false,
    label: "Oluşturan Profil",
  },
  // { id: 'description', numeric: false, disablePadding: false, label: 'Açıklama' },
  {
    id: "isProtected",
    numeric: false,
    disablePadding: false,
    label: "Koruma Durumu",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "Eylemler",
    sortable: false,
  },
];

const PinManagementPage = () => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const { currentUser } = useAuth();

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditingPin, setCurrentEditingPin] = useState(null);

  const [newPinTitle, setNewPinTitle] = useState("");
  const [newPinDesc, setNewPinDesc] = useState("");
  const [newPinLat, setNewPinLat] = useState("");
  const [newPinLng, setNewPinLng] = useState("");
  const [newPinAudioUrl, setNewPinAudioUrl] = useState("");
  const [newPinTier, setNewPinTier] = useState("Standard");
  const [newPinStatus, setNewPinStatus] = useState("active");
  const [newPinIsPromoted, setNewPinIsPromoted] = useState(false);
  const [selectedPinOwner, setSelectedPinOwner] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [audioInputType, setAudioInputType] = useState("url");
  const [audioUploading, setAudioUploading] = useState(false);
  const [isProtected, setIsProtected] = useState(false);
  const [pinCode, setPinCode] = useState("");
  const [formTitleError, setFormTitleError] = useState("");
  const [formLatError, setFormLatError] = useState("");
  const [formLngError, setFormLngError] = useState("");

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [pinToDeleteId, setPinToDeleteId] = useState(null);

  // Filtre State'leri
  const [searchTerm, setSearchTerm] = useState("");
  const [protectionFilter, setProtectionFilter] = useState("all");
  const [pinTierFilter, setPinTierFilter] = useState("all");
  const [pinStatusFilter, setPinStatusFilter] = useState("all");
  const [pinTypeFilter, setPinTypeFilter] = useState("all");
  const [quarantineFilter, setQuarantineFilter] = useState("all");
  const [creationDateFrom, setCreationDateFrom] = useState("");
  const [creationDateTo, setCreationDateTo] = useState("");
  const [creatorProfileFilter, setCreatorProfileFilter] = useState("");
  const [moderationStatusFilter, setModerationStatusFilter] = useState("all");

  const resetFilters = useCallback(() => {
    setSearchTerm("");
    setProtectionFilter("all");
    setPinTierFilter("all");
    setPinStatusFilter("all");
    setPinTypeFilter("all");
    setQuarantineFilter("all");
    setCreationDateFrom("");
    setCreationDateTo("");
    setCreatorProfileFilter("");
    setModerationStatusFilter("all");
  }, []);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const geocodeCacheRef = useRef(new Map());

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [selected, setSelected] = useState([]);

  // Bulk Edit Modal State and Handlers
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState({
    status: "nochange",
    isProtected: "nochange",
    pinTier: "nochange",
  });

  const handleOpenBulkEditModal = () => {
    setBulkEditValues({
      status: "nochange",
      isProtected: "nochange",
      pinTier: "nochange",
    });
    setOpenBulkEditModal(true);
  };

  const handleCloseBulkEditModal = () => {
    setOpenBulkEditModal(false);
  };

  const handleBulkEditChange = (event) => {
    const { name, value } = event.target;
    setBulkEditValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyBulkEdit = async () => {
    if (selected.length === 0) {
      showSnackbar("Toplu düzenleme için pin seçilmedi.", "warning");
      return;
    }

    const promises = [];
    for (const pinId of selected) {
      const pinToUpdate = pins.find((p) => p.id === pinId);
      if (!pinToUpdate) continue;

      const updates = {};
      if (bulkEditValues.status !== "nochange") {
        updates.status = bulkEditValues.status;
      }
      if (bulkEditValues.isProtected !== "nochange") {
        updates.isProtected = bulkEditValues.isProtected === "true"; // Convert string to boolean
      }
      if (bulkEditValues.pinTier !== "nochange") {
        updates.pinTier = bulkEditValues.pinTier;
      }

      if (Object.keys(updates).length > 0) {
        // Ensure coordinates are saved as numbers
        updates.latitude = parseFloat(pinToUpdate.latitude);
        updates.longitude = parseFloat(pinToUpdate.longitude);
        updates.updatedAt = serverTimestamp();
        promises.push(
          updateDoc(doc(db, "pins", pinId), updates)
            .then(() => ({ id: pinId, status: "fulfilled" }))
            .catch((error) => ({ id: pinId, status: "rejected", error }))
        );
      }
    }

    if (promises.length === 0) {
      showSnackbar("Değişiklik yapılacak bir alan seçilmedi.", "info");
      handleCloseBulkEditModal();
      return;
    }

    setLoading(true);
    const results = await Promise.allSettled(promises);
    setLoading(false);

    let successCount = 0;
    let errorCount = 0;

    results.forEach((result) => {
      if (
        result.status === "fulfilled" &&
        result.value.status === "fulfilled"
      ) {
        successCount++;
      } else if (
        result.status === "rejected" ||
        (result.value && result.value.status === "rejected")
      ) {
        errorCount++;
        const pinId = result.value ? result.value.id : "Bilinmeyen";
        const error = result.value ? result.value.error : result.reason;
        console.error(`Error updating pin ${pinId}:`, error);
      }
    });

    if (successCount > 0) {
      showSnackbar(`${successCount} pin başarıyla güncellendi.`, "success");
      fetchPins(); // Refresh the pins list
    }
    if (errorCount > 0) {
      showSnackbar(
        `${errorCount} pin güncellenirken hata oluştu. Lütfen konsolu kontrol edin.`,
        "error"
      );
    }
    if (successCount === 0 && errorCount === 0 && promises.length > 0) {
      showSnackbar(
        "Pin güncelleme işlemi tamamlandı, ancak değişiklik olmadı veya durum belirsiz.",
        "info"
      );
    }

    handleCloseBulkEditModal();
    setSelected([]); // Clear selection after applying
  };

  // Bulk Delete Dialog State and Handlers
  const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);

  const handleOpenBulkDeleteDialog = () => {
    if (selected.length === 0) {
      showSnackbar("Silmek için en az bir pin seçmelisiniz.", "warning");
      return;
    }
    setOpenBulkDeleteDialog(true);
  };

  const handleCloseBulkDeleteDialog = () => {
    setOpenBulkDeleteDialog(false);
  };

  const handleApplyBulkDelete = async () => {
    if (selected.length === 0) {
      showSnackbar("Silmek için pin seçilmedi.", "warning");
      handleCloseBulkDeleteDialog();
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall("/api/admin/pins/bulk-delete", {
        method: "POST",
        body: JSON.stringify({ ids: selected }),
      });
      const summary = response.summary || {
        deleted: 0,
        notFound: 0,
        failed: 0,
      };
      setLoading(false);
      const message = `Silme tamamlandı. Silinen: ${summary.deleted}, Bulunamadı: ${summary.notFound}, Hata: ${summary.failed}`;
      showSnackbar(message, summary.failed === 0 ? "success" : "warning");
      fetchPins();
      handleCloseBulkDeleteDialog();
      setSelected([]);
    } catch (e) {
      console.error("Bulk delete failed:", e);
      // Fallback: endpoint yoksa (404) tek tek silmeye dön
      if (e?.response?.status === 404) {
        try {
          const results = await Promise.allSettled(
            selected.map(async (pinId) => {
              try {
                const response = await apiCall(`/api/pins/${pinId}`, {
                  method: "DELETE",
                });
                return { id: pinId, ok: response.success === true };
              } catch (err) {
                if (err.message?.includes("404")) {
                  return { id: pinId, ok: true }; // idempotent success
                }
                throw err;
              }
            })
          );
          const deleted = results.filter(
            (r) => r.status === "fulfilled" && r.value.ok
          ).length;
          const failed = results.length - deleted;
          showSnackbar(
            `${deleted} pin silindi. Hata: ${failed}`,
            deleted > 0 ? "success" : "error"
          );
          fetchPins();
          handleCloseBulkDeleteDialog();
          setSelected([]);
        } catch (e2) {
          console.error("Fallback individual delete failed:", e2);
          showSnackbar(
            e2?.response?.data?.error || "Toplu silme başarısız.",
            "error"
          );
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
        showSnackbar(
          e?.response?.data?.error || "Toplu silme başarısız.",
          "error"
        );
      }
    }
  };

  const fetchPins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall("/api/pins", "GET");
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch pins");
      }

      const fetchedPins = [];
      (response.pins || []).forEach((pinData) => {
        const data = pinData;
        let warnings = [];

        // Güvenli parse: location
        let latitude = null;
        let longitude = null;
        if (
          typeof data.latitude === "number" &&
          typeof data.longitude === "number"
        ) {
          // Direct latitude/longitude fields (most common case)
          latitude = data.latitude;
          longitude = data.longitude;
        } else if (
          data.location &&
          typeof data.location.latitude === "number" &&
          typeof data.location.longitude === "number"
        ) {
          // GeoPoint format
          latitude = data.location.latitude;
          longitude = data.location.longitude;
        } else {
          // String veya array ise parse etmeye çalış
          if (typeof data.latitude === "string")
            latitude = parseFloat(data.latitude);
          if (typeof data.longitude === "string")
            longitude = parseFloat(data.longitude);
          if (Array.isArray(data.location) && data.location.length === 2) {
            latitude = parseFloat(data.location[0]);
            longitude = parseFloat(data.location[1]);
            warnings.push("location array olarak geldi, parse edildi");
          }
          if (!latitude || !longitude) {
            warnings.push("Enlem/boylam eksik veya hatalı");
            latitude = 0;
            longitude = 0;
          }
        }

        // Diğer alanlar için fallback
        const pin = {
          id: data.id || pinData.id,
          title: data.title || data.name || data.isim || "Eksik",
          pinType: data.pinType || (data.audioUrl ? "audio" : "text"),
          pinTier: data.pinTier || "Free",
          status: statusMapping[data.status] || data.status || "Aktif", // Backend status normalizasyonu
          createdAt: data.createdAt ? new Date(data.createdAt) : null,
          deletedAt: data.deletedAt ? new Date(data.deletedAt) : null,
          undoDeadline: data.undoDeadline ? new Date(data.undoDeadline) : null,
          latitude,
          longitude,
          isProtected:
            typeof data.isProtected === "boolean" ? data.isProtected : false,
          description: data.description || "",
          audioUrl: data.audioUrl || "",
          imageUrl: data.imageUrl || "",
          pinCode: data.pinCode || "",
          pinOwnerName: data.pinOwnerName || "",
          pinOwnerPhotoUrl: data.pinOwnerPhotoUrl || "",
          commentCount:
            typeof data.commentCount === "number"
              ? data.commentCount
              : parseInt(data.commentCount) || 0,
          reportCount:
            typeof data.reportCount === "number"
              ? data.reportCount
              : parseInt(data.reportCount) || 0,
          isQuarantined:
            typeof data.isQuarantined === "boolean"
              ? data.isQuarantined
              : false,
          reportedByUserIds: Array.isArray(data.reportedByUserIds)
            ? data.reportedByUserIds
            : [],
          isPromoted:
            typeof data.isPromoted === "boolean" ? data.isPromoted : false,
          moderationStatus: data.moderationStatus || "pending",
          warnings,
          city: "",
          country: "",
        };
        if (warnings.length > 0) {
          console.warn(`[PIN FETCH][${pin.id}]`, warnings.join("; "), pin);
        }
        fetchedPins.push(pin);
      });

      // Reverse-geocode city/country (SADECE eksik olanlar için)
      // Backend'den zaten city/country geliyorsa, API çağrısı yapma!
      if (GOOGLE_MAPS_API_KEY) {
        // Sadece city/country eksik olan pinleri filtrele
        const pinsNeedingGeocode = fetchedPins.filter(
          (p) => p.latitude && p.longitude && (!p.city || !p.country)
        );

        if (pinsNeedingGeocode.length > 0) {
          console.log(
            `🗺️  Geocoding ${pinsNeedingGeocode.length}/${fetchedPins.length} pins...`
          );

          // Rate limiting için batch'lere ayır (50 pin/batch)
          const BATCH_SIZE = 50;
          const batches = [];
          for (let i = 0; i < pinsNeedingGeocode.length; i += BATCH_SIZE) {
            batches.push(pinsNeedingGeocode.slice(i, i + BATCH_SIZE));
          }

          // Her batch'i sırayla işle
          for (const batch of batches) {
            const tasks = batch.map(async (p) => {
              const key = `${p.latitude.toFixed(3)},${p.longitude.toFixed(3)}`;

              // Cache kontrolü
              if (geocodeCacheRef.current.has(key)) {
                const cached = geocodeCacheRef.current.get(key);
                p.city = cached.city || "";
                p.country = cached.country || "";
                return p;
              }

              // Google Maps API çağrısı
              try {
                const resp = await fetch(
                  `https://maps.googleapis.com/maps/api/geocode/json?latlng=${p.latitude},${p.longitude}&key=${GOOGLE_MAPS_API_KEY}`
                );
                const j = await resp.json();
                if (Array.isArray(j.results) && j.results.length > 0) {
                  const comps = j.results[0].address_components || [];
                  const city =
                    (
                      comps.find((c) =>
                        c.types.includes("administrative_area_level_1")
                      ) ||
                      comps.find((c) => c.types.includes("locality")) ||
                      {}
                    ).long_name || "";
                  const country =
                    (comps.find((c) => c.types.includes("country")) || {})
                      .long_name || "";
                  p.city = city;
                  p.country = country;
                  geocodeCacheRef.current.set(key, { city, country });
                }
              } catch (err) {
                console.warn(`Geocode error for pin ${p.id}:`, err.message);
              }
              return p;
            });

            // Batch'i bekle (rate limiting için delay eklenebilir)
            await Promise.allSettled(tasks);

            // Her batch arasında kısa bir delay (rate limit koruması)
            if (batches.indexOf(batch) < batches.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }
        } else {
          console.log("✅ All pins already have city/country data");
        }
      }

      setPins(fetchedPins);
    } catch (error) {
      console.error("Pinler yüklenirken hata oluştu:", error);
      setError("Pinler yüklenemedi. Lütfen daha sonra tekrar deneyin.");
      setSnackbarMessage(
        "Pinler yüklenirken bir hata oluştu. Teknik detay için konsolu kontrol edin."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    setLoading(false);
  }, []);

  // DEBUG: Ekrana pin veri yapısını göster
  // (Tablonun üstüne veya altına ekleyebilirsin)
  // <pre>{JSON.stringify(pins, null, 2)}</pre>

  useEffect(() => {
    fetchPins();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiCall("/api/admin/users");
      console.log("Users API response:", response); // Debug log

      // Add default adPin user
      const defaultUser = {
        id: "adPin_user_id",
        name: "adPin",
        email: "admin@putpin.org",
        photoUrl:
          "https://firebasestorage.googleapis.com/v0/b/echomapx.firebasestorage.app/o/logos%2Flogo.png?alt=media&token=c5529575-e68f-4a1c-b1ee-54cb3b7ce62c",
      };

      if (response && response.success && Array.isArray(response.users)) {
        const users = response.users.map((user) => ({
          id: user.id,
          name: user.displayName || user.email,
          email: user.email,
          photoUrl: user.photoURL,
        }));
        setAvailableUsers([defaultUser, ...users]);
        setSelectedPinOwner(defaultUser);
      } else {
        // Fallback: just use default user if API fails
        console.warn("No users returned from API, using default only");
        setAvailableUsers([defaultUser]);
        setSelectedPinOwner(defaultUser);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      // Fallback on error
      const defaultUser = {
        id: "adPin_user_id",
        name: "adPin",
        email: "admin@putpin.org",
        photoUrl:
          "https://firebasestorage.googleapis.com/v0/b/echomapx.firebasestorage.app/o/logos%2Flogo.png?alt=media&token=c5529575-e68f-4a1c-b1ee-54cb3b7ce62c",
      };
      setAvailableUsers([defaultUser]);
      setSelectedPinOwner(defaultUser);
    }
  };

  const handleAudioFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "audio/mpeg",
      "audio/wav",
      "audio/mp4",
      "audio/m4a",
      "audio/ogg",
      "audio/mp3",
      "audio/aac",
    ];
    const allowedExtensions = [".mp3", ".wav", ".mp4", ".m4a", ".ogg", ".aac"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      showSnackbar(
        "Desteklenen ses formatları: MP3, WAV, MP4, M4A, OGG, AAC",
        "error"
      );
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      // 50MB limit
      showSnackbar("Ses dosyası 50MB'dan küçük olmalıdır.", "error");
      return;
    }

    try {
      setAudioUploading(true);
      const fileName = `audio/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);

      const metadata = {
        contentType:
          file.type === "audio/aac" || file.name.toLowerCase().endsWith(".aac")
            ? "audio/aac"
            : file.type,
      };

      await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(storageRef);

      setNewPinAudioUrl(downloadURL);
      showSnackbar("Ses dosyası başarıyla yüklendi", "success");
    } catch (error) {
      console.error("Ses dosyası yükleme hatası:", error);
      console.error("Error details:", error.code, error.message);
      showSnackbar(
        `Ses dosyası yüklenirken hata oluştu: ${error.message}`,
        "error"
      );
    } finally {
      setAudioUploading(false);
    }
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleOpenAddDialog = () => {
    setIsEditMode(false);
    setCurrentEditingPin(null);
    setNewPinTitle("");
    setNewPinDesc("");
    setNewPinLat("");
    setNewPinLng("");
    setNewPinAudioUrl("");
    setNewPinTier("Standard");
    setNewPinStatus("active");
    setNewPinIsPromoted(false);
    setAudioInputType("url");
    // Reset to default adPin user
    const defaultUser = availableUsers.find((u) => u.id === "adPin_user_id");
    setSelectedPinOwner(defaultUser || null);
    setIsProtected(false);
    setPinCode("");
    setFormTitleError("");
    setFormLatError("");
    setFormLngError("");
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (pin) => {
    setIsEditMode(true);
    setCurrentEditingPin(pin);
    setNewPinTitle(pin.title || "");
    setNewPinDesc(pin.description || "");
    setNewPinLat(pin.latitude?.toString() || "");
    setNewPinLng(pin.longitude?.toString() || "");
    setNewPinAudioUrl(pin.audioUrl || "");
    setNewPinTier(pin.pinTier || "Standard");
    setNewPinStatus(pin.status || "Aktif"); // Frontend formatında
    setNewPinIsPromoted(pin.isPromoted || false);
    setAudioInputType(pin.audioUrl ? "url" : "upload");
    // Pin sahibini bul ve set et (hem userId hem pinOwnerId denemesi)
    const owner = availableUsers.find(
      (u) => u.id === pin.userId || u.id === pin.pinOwnerId
    );
    setSelectedPinOwner(owner || null);
    setIsProtected(pin.isProtected || false);
    setPinCode(pin.pinCode || "");
    setFormTitleError("");
    setFormLatError("");
    setFormLngError("");
    setOpenAddDialog(true);
  };

  const handlePromotedToggle = async (pinId, isPromoted) => {
    try {
      const pinRef = doc(db, "pins", pinId);
      await updateDoc(pinRef, {
        isPromoted: isPromoted,
      });

      // Local state'i güncelle
      setPins((prevPins) =>
        prevPins.map((pin) => (pin.id === pinId ? { ...pin, isPromoted } : pin))
      );

      showSnackbar(
        `Pin ${
          isPromoted ? "reklam pini yapıldı" : "reklam pininden çıkarıldı"
        }!`,
        "success"
      );
    } catch (error) {
      console.error("Promoted toggle error:", error);
      showSnackbar("Reklam pini durumu güncellenirken hata oluştu!", "error");
    }
  };

  const handleResetQuarantine = async (pinId) => {
    try {
      const pinRef = doc(db, "pins", pinId);
      await updateDoc(pinRef, {
        isQuarantined: false,
        reportCount: 0,
        reportedByUserIds: [],
      });

      // Local state'i güncelle
      setPins((prevPins) =>
        prevPins.map((pin) =>
          pin.id === pinId
            ? {
                ...pin,
                isQuarantined: false,
                reportCount: 0,
                reportedByUserIds: [],
              }
            : pin
        )
      );

      showSnackbar("Pin karantinası başarıyla sıfırlandı!", "success");
    } catch (error) {
      console.error("Quarantine reset error:", error);
      showSnackbar("Karantina sıfırlanırken hata oluştu!", "error");
    }
  };

  const handleCloseFormDialog = () => {
    setOpenAddDialog(false);
    setIsEditMode(false);
    setCurrentEditingPin(null);
    // Reset form fields
    setNewPinTitle("");
    setNewPinDesc("");
    setNewPinLat("");
    setNewPinLng("");
    setNewPinAudioUrl("");
    setNewPinTier("Standard");
    setNewPinStatus("active");
    setNewPinIsPromoted(false);
    setSelectedPinOwner(null);
    setIsProtected(false);
    setPinCode("");
    setFormTitleError("");
    setFormLatError("");
    setFormLngError("");
  };

  const handleAddNewPin = async () => {
    let hasError = false;
    if (!newPinTitle) {
      setFormTitleError("Başlık gerekli");
      hasError = true;
    } else {
      setFormTitleError("");
    }
    if (!newPinLat) {
      setFormLatError("Enlem gerekli");
      hasError = true;
    } else {
      setFormLatError("");
    }
    if (!newPinLng) {
      setFormLngError("Boylam gerekli");
      hasError = true;
    } else {
      setFormLngError("");
    }

    if (hasError) {
      showSnackbar("Lütfen gerekli alanları doldurun.", "error");
      return;
    }

    // Koordinatları daima sayıya çevir ve tip kontrolü yap
    const latitude = parseFloat(newPinLat);
    const longitude = parseFloat(newPinLng);
    if (isNaN(latitude) || isNaN(longitude)) {
      showSnackbar("Geçersiz koordinat formatı!", "error");
      return;
    }
    const geoPoint = new GeoPoint(latitude, longitude);
    if (!(geoPoint instanceof GeoPoint)) {
      showSnackbar("GeoPoint nesnesi oluşturulamadı!", "error");
      return;
    }
    const pinData = {
      title: newPinTitle,
      description: newPinDesc,
      location: geoPoint, // Sadece GeoPoint!
      latitude: latitude,
      longitude: longitude,
      userId: selectedPinOwner?.id || "adPin_user_id",
      pinOwnerName: selectedPinOwner?.name || "adPin",
      pinOwnerPhotoUrl:
        selectedPinOwner?.photoUrl ||
        "https://firebasestorage.googleapis.com/v0/b/echomapx.firebasestorage.app/o/logos%2Flogo.png?alt=media&token=c5529575-e68f-4a1c-b1ee-54cb3b7ce62c",
      updatedAt: serverTimestamp(),
      isProtected: isProtected,
      pinCode: isProtected ? pinCode : "",
      audioUrl: newPinAudioUrl || "",
      pinTier: newPinTier,
      status: newPinStatus,
      isPromoted: newPinIsPromoted,
      isQuarantined: false,
      reportCount: 0,
      reportedByUserIds: [],
      moderationStatus: "pending",
    };

    // Son kontrol: location alanı yanlışlıkla string/array ise hata ver ve işlemi durdur
    if (
      typeof pinData.location === "string" ||
      Array.isArray(pinData.location)
    ) {
      showSnackbar(
        "Pin için location alanı yanlış formatta! (Sadece GeoPoint olmalı)",
        "error"
      );
      return;
    }

    try {
      if (isEditMode && currentEditingPin) {
        const pinRef = doc(db, "pins", currentEditingPin.id);
        // createdAt alanını güncelleme, sadece var olanı koru
        const dataToUpdate = { ...pinData };
        // createdAt'i hiç değiştirme (Firestore'da zaten var)
        delete dataToUpdate.createdAt;
        await updateDoc(pinRef, dataToUpdate);
        showSnackbar("Pin başarıyla güncellendi!", "success");
      } else {
        await addDoc(collection(db, "pins"), {
          ...pinData,
          createdAt: serverTimestamp(),
        });
        showSnackbar("Pin başarıyla eklendi!", "success");
      }
      fetchPins();
      handleCloseFormDialog();
    } catch (error) {
      console.error("Error adding/updating pin: ", error);
      showSnackbar(`Hata: ${error.message}`, "error");
    }
  };

  const handleOpenDeleteDialog = (id) => {
    setPinToDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setPinToDeleteId(null);
  };

  const handleDeletePin = async () => {
    if (!pinToDeleteId) return;
    try {
      const response = await apiCall(`/api/pins/${pinToDeleteId}`, {
        method: "DELETE",
      });

      if (response.success) {
        showSnackbar("Silme tamamlandı.", "success");
        fetchPins(); // Refresh the pin list from the server
        handleCloseDeleteDialog();
        setSelected((prevSelected) =>
          prevSelected.filter((id) => id !== pinToDeleteId)
        );
      } else {
        // Handle specific backend error message
        showSnackbar(
          response.error || "Pin silinirken bir hata oluştu.",
          "error"
        );
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        showSnackbar("Pin zaten mevcut değil; listeden kaldırıldı.", "success");
        fetchPins();
        handleCloseDeleteDialog();
        setSelected((prevSelected) =>
          prevSelected.filter((id) => id !== pinToDeleteId)
        );
        return;
      }
      console.error("Error deleting pin: ", error);
      showSnackbar(
        error?.response?.data?.error ||
          error.message ||
          "Sunucuyla iletişim kurulamadı.",
        "error"
      );
    }
  };

  const handleUndoPin = async (pinId) => {
    try {
      const resp = await apiCall(`/api/pins/${pinId}/undo`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (resp.success) {
        showSnackbar("Pin geri yüklendi.", "success");
        fetchPins();
      } else {
        showSnackbar(resp.error || "Geri alma başarısız.", "error");
      }
    } catch (e) {
      showSnackbar(
        e?.response?.data?.error || e.message || "Geri alma başarısız.",
        "error"
      );
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  const handleProtectionFilterChange = (event) => {
    setProtectionFilter(event.target.value);
    setPage(0);
  };
  const handlePinTierFilterChange = (event) => {
    setPinTierFilter(event.target.value);
    setPage(0);
  };
  const handlePinStatusFilterChange = (event) => {
    setPinStatusFilter(event.target.value);
    setPage(0);
  };
  const handleCreationDateFromChange = (event) => {
    setCreationDateFrom(event.target.value);
    setPage(0);
  };
  const handleCreationDateToChange = (event) => {
    setCreationDateTo(event.target.value);
    setPage(0);
  };
  const handleCreatorProfileFilterChange = (event) => {
    setCreatorProfileFilter(event.target.value);
    setPage(0);
  };
  const handleModerationStatusFilterChange = (event) => {
    setModerationStatusFilter(event.target.value);
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredPins.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleRowCheckboxClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0)
      newSelected = newSelected.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1)
      newSelected = newSelected.concat(selected.slice(0, -1));
    else if (selectedIndex > 0)
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const filteredPins = pins.filter((pin) => {
    const searchTermLower = searchTerm.toLowerCase();
    const titleMatch = pin.title?.toLowerCase().includes(searchTermLower);
    const descriptionMatch = pin.description
      ?.toLowerCase()
      .includes(searchTermLower);
    const searchMatch = titleMatch || descriptionMatch;

    const protectionMatch =
      protectionFilter === "all" ||
      (protectionFilter === "protected" && pin.isProtected) ||
      (protectionFilter === "unprotected" && !pin.isProtected);

    const tierMatch = pinTierFilter === "all" || pin.pinTier === pinTierFilter;
    const statusMatch =
      pinStatusFilter === "all" || pin.status === pinStatusFilter;
    const typeMatch = pinTypeFilter === "all" || pin.pinType === pinTypeFilter;

    // Karantina filtresi
    const quarantineMatch =
      quarantineFilter === "all" ||
      quarantineFilter === String(pin.reportCount || 0);

    // Oluşturan profil filtresi
    const creatorMatch =
      !creatorProfileFilter ||
      pin.pinOwnerName
        ?.toLowerCase()
        .includes(creatorProfileFilter.toLowerCase());

    // Tarih filtresi (düzeltildi: backend Date object gönderiyor)
    let dateMatch = true;
    if (creationDateFrom || creationDateTo) {
      // Eğer tarih filtresi aktifse
      if (!pin.createdAt || !(pin.createdAt instanceof Date)) {
        // Pin'de createdAt yoksa veya Date değilse, filtreyi geçemez
        dateMatch = false;
      } else {
        const pinCreationDate = pin.createdAt;

        if (creationDateFrom) {
          const fromDate = new Date(creationDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (pinCreationDate < fromDate) {
            dateMatch = false;
          }
        }

        if (creationDateTo && dateMatch) {
          const toDate = new Date(creationDateTo);
          toDate.setHours(23, 59, 59, 999);
          if (pinCreationDate > toDate) {
            dateMatch = false;
          }
        }
      }
    }

    // Moderasyon durumu filtresi
    const moderationStatusMatch =
      moderationStatusFilter === "all" ||
      pin.moderationStatus === moderationStatusFilter;

    return (
      searchMatch &&
      protectionMatch &&
      tierMatch &&
      statusMatch &&
      typeMatch &&
      quarantineMatch &&
      creatorMatch &&
      dateMatch &&
      moderationStatusMatch
    );
  });

  const visiblePins = stableSort(
    filteredPins,
    getComparator(order, orderBy)
  ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 1400,
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography variant="h4" gutterBottom component="div" sx={{ mb: 3 }}>
          Pin Yönetimi
        </Typography>

        <Paper elevation={1} sx={{ mb: 3, p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Pin Başlığı/Açıklamasında Ara"
                fullWidth
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Koruma Durumu</InputLabel>
                <Select
                  value={protectionFilter}
                  onChange={handleProtectionFilterChange}
                  label="Koruma Durumu"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="protected">Korumalı</MenuItem>
                  <MenuItem value="unprotected">Korumasız</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Pin Seviyesi</InputLabel>
                <Select
                  value={pinTierFilter}
                  onChange={handlePinTierFilterChange}
                  label="Pin Seviyesi"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  {pinTierOptions.map((tier) => (
                    <MenuItem key={tier} value={tier}>
                      {tier}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Durumu</InputLabel>
                <Select
                  value={pinStatusFilter}
                  onChange={handlePinStatusFilterChange}
                  label="Durumu"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  {pinStatusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Pin Türü</InputLabel>
                <Select
                  value={pinTypeFilter}
                  label="Pin Türü"
                  onChange={(e) => setPinTypeFilter(e.target.value)}
                >
                  {pinTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Şikayet Sayısı</InputLabel>
                <Select
                  value={quarantineFilter}
                  onChange={(e) => setQuarantineFilter(e.target.value)}
                  label="Şikayet Sayısı"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  {[...Array(12)].map((_, i) => (
                    <MenuItem key={i} value={String(i)}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Oluşturan Profil"
                fullWidth
                value={creatorProfileFilter}
                onChange={handleCreatorProfileFilterChange}
                placeholder="Profil adı ara..."
              />
            </Grid>
            <Grid
              item
              xs={12}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button onClick={resetFilters} variant="outlined">
                Filtreleri Sıfırla
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Oluşturulma (Başlangıç)"
                type="date"
                fullWidth
                value={creationDateFrom}
                onChange={handleCreationDateFromChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Oluşturulma (Bitiş)"
                type="date"
                fullWidth
                value={creationDateTo}
                onChange={handleCreationDateToChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Denetim Durumu</InputLabel>
                <Select
                  value={moderationStatusFilter}
                  onChange={handleModerationStatusFilterChange}
                  label="Denetim Durumu"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="approved">Onaylandı</MenuItem>
                  <MenuItem value="review_required">İncelemede</MenuItem>
                  <MenuItem value="quarantined_manual">Karantinada</MenuItem>
                  <MenuItem value="pending">Bekliyor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={2}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenAddDialog}
                fullWidth
              >
                Yeni Ekle
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {selected.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              mb: 3,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1">
              {selected.length} pin seçildi
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handleOpenBulkEditModal}>
                Seçilenleri Toplu Düzenle
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleOpenBulkDeleteDialog}
              >
                Seçilenleri Toplu Sil
              </Button>
            </Box>
          </Paper>
        )}

        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}
        >
          <Table
            sx={{ minWidth: 1000, tableLayout: "auto" }}
            aria-labelledby="tableTitle"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < filteredPins.length
                    }
                    checked={
                      filteredPins.length > 0 &&
                      selected.length === filteredPins.length
                    }
                    onChange={handleSelectAllClick}
                    inputProps={{ "aria-label": "select all filtered pins" }}
                  />
                </TableCell>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? "right" : "left"}
                    padding={headCell.disablePadding ? "none" : "normal"}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable !== false ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={(event) =>
                          handleRequestSort(event, headCell.id)
                        }
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visiblePins.map((pin, index) => {
                const isItemSelected = isSelected(pin.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    onClick={(event) => handleRowCheckboxClick(event, pin.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={pin.id}
                    selected={isItemSelected}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{ "aria-labelledby": labelId }}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) =>
                          handleRowCheckboxClick(event, pin.id)
                        }
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {pin.title}
                    </TableCell>
                    <TableCell>
                      {pin.description ? (
                        <Box
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {pin.description}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {pin.imageUrl ? (
                        <Avatar
                          src={pin.imageUrl}
                          alt="Pin Image"
                          variant="rounded"
                          sx={{ width: 48, height: 48 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{pin.id}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          pin.pinType === "audio"
                            ? "Sesli"
                            : pin.pinType === "text"
                            ? "Metin"
                            : pin.audioUrl
                            ? "Sesli"
                            : "Metin"
                        }
                        color={
                          pin.pinType === "audio" || pin.audioUrl
                            ? "primary"
                            : "default"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          pin.isPromoted
                            ? "Reklam"
                            : pin.isGlobal
                            ? "Global"
                            : "Normal"
                        }
                        color={
                          pin.isPromoted
                            ? "warning"
                            : pin.isGlobal
                            ? "primary"
                            : "default"
                        }
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{pin.pinTier || "N/A"}</TableCell>
                    <TableCell>{pin.status || "N/A"}</TableCell>
                    <TableCell>
                      <Switch
                        checked={pin.isPromoted || false}
                        onChange={(e) =>
                          handlePromotedToggle(pin.id, e.target.checked)
                        }
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              pin.reportCount >= 11
                                ? "error.main"
                                : "text.primary",
                            fontWeight:
                              pin.reportCount >= 11 ? "bold" : "normal",
                          }}
                        >
                          {pin.reportCount || 0}
                        </Typography>
                        {pin.isQuarantined && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResetQuarantine(pin.id);
                            }}
                            size="small"
                            color="warning"
                            variant="outlined"
                          >
                            Sıfırla
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(pin.createdAt)}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          pin.moderationStatus === "approved"
                            ? "Onaylandı"
                            : pin.moderationStatus === "review_required"
                            ? "İncelemede"
                            : pin.moderationStatus === "quarantined_manual"
                            ? "Karantinada"
                            : "Bekliyor"
                        }
                        color={
                          pin.moderationStatus === "approved"
                            ? "success"
                            : pin.moderationStatus === "review_required"
                            ? "warning"
                            : pin.moderationStatus === "quarantined_manual"
                            ? "error"
                            : "default"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {typeof pin.latitude === "number"
                        ? pin.latitude.toFixed(4)
                        : "N/A"}
                    </TableCell>
                    <TableCell align="right">
                      {typeof pin.longitude === "number"
                        ? pin.longitude.toFixed(4)
                        : "N/A"}
                    </TableCell>
                    <TableCell>{pin.city || ""}</TableCell>
                    <TableCell>{pin.country || ""}</TableCell>
                    <TableCell>{pin.pinOwnerName || "N/A"}</TableCell>
                    <TableCell>
                      <Chip
                        label={pin.isProtected ? "Korumalı" : "Korumasız"}
                        color={pin.isProtected ? "secondary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/pins/${pin.id}`, "_blank");
                          }}
                          size="small"
                          variant="outlined"
                          color="primary"
                        >
                          Göster
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditDialog(pin);
                          }}
                          size="small"
                        >
                          Düzenle
                        </Button>
                        {pin.deletedAt ? (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUndoPin(pin.id);
                            }}
                            size="small"
                            color="secondary"
                          >
                            Geri Al
                          </Button>
                        ) : (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDeleteDialog(pin.id);
                            }}
                            size="small"
                            color="error"
                          >
                            Sil
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {visiblePins.length === 0 && (
                <TableRow>
                  <TableCell colSpan={headCells.length + 1} align="center">
                    Filtrelerle eşleşen pin bulunamadı.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredPins.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Sayfa başına satır:"
        />

        {/* Add/Edit Pin Dialog */}
        <Dialog open={openAddDialog} onClose={handleCloseFormDialog}>
          <DialogTitle>
            {isEditMode ? "Pin Düzenle" : "Yeni Pin Ekle"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {isEditMode
                ? "Pinin bilgilerini güncelleyin."
                : "Yeni bir pin eklemek için lütfen aşağıdaki bilgileri doldurun."}
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="title"
              label="Pin Başlığı"
              type="text"
              fullWidth
              value={newPinTitle}
              onChange={(e) => setNewPinTitle(e.target.value)}
              error={!!formTitleError}
              helperText={formTitleError}
            />
            <TextField
              margin="dense"
              id="description"
              label="Açıklama"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={newPinDesc}
              onChange={(e) => setNewPinDesc(e.target.value)}
            />

            <FormControl fullWidth margin="dense">
              <InputLabel id="pin-tier-select-label">Pin Seviyesi</InputLabel>
              <Select
                labelId="pin-tier-select-label"
                id="pinTier"
                value={newPinTier}
                onChange={(e) => setNewPinTier(e.target.value)}
                label="Pin Seviyesi"
              >
                {pinTierOptions.map((tier) => (
                  <MenuItem key={tier} value={tier}>
                    {tier}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="dense">
              <InputLabel id="pin-status-select-label">Durumu</InputLabel>
              <Select
                labelId="pin-status-select-label"
                id="pinStatus"
                value={newPinStatus}
                onChange={(e) => setNewPinStatus(e.target.value)}
                label="Durumu"
              >
                {pinStatusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={newPinIsPromoted}
                  onChange={(e) => setNewPinIsPromoted(e.target.checked)}
                  color="primary"
                />
              }
              label="Reklam Pini (Listelerde öncelikli gösterilir)"
              sx={{ mt: 2, mb: 1 }}
            />

            <TextField
              margin="dense"
              id="lat"
              label="Enlem"
              type="number"
              fullWidth
              value={newPinLat}
              onChange={(e) => setNewPinLat(e.target.value)}
              error={!!formLatError}
              helperText={formLatError}
            />
            <TextField
              margin="dense"
              id="lng"
              label="Boylam"
              type="number"
              fullWidth
              value={newPinLng}
              onChange={(e) => setNewPinLng(e.target.value)}
              error={!!formLngError}
              helperText={formLngError}
            />
            <FormControl component="fieldset" margin="dense">
              <FormLabel component="legend">Ses Dosyası (Opsiyonel)</FormLabel>
              <RadioGroup
                row
                value={audioInputType}
                onChange={(e) => {
                  setAudioInputType(e.target.value);
                  setNewPinAudioUrl("");
                }}
                sx={{ mb: 2 }}
              >
                <FormControlLabel value="url" control={<Radio />} label="URL" />
                <FormControlLabel
                  value="file"
                  control={<Radio />}
                  label="Dosya Yükle"
                />
              </RadioGroup>

              {audioInputType === "url" ? (
                <TextField
                  fullWidth
                  label="Ses URL'si"
                  type="url"
                  value={newPinAudioUrl}
                  onChange={(e) => setNewPinAudioUrl(e.target.value)}
                  placeholder="https://example.com/audio.mp3"
                />
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={audioUploading}
                  >
                    {audioUploading ? "Yükleniyor..." : "Ses Dosyası Seç"}
                    <input
                      type="file"
                      hidden
                      accept="audio/*"
                      onChange={handleAudioFileUpload}
                    />
                  </Button>
                  {audioUploading && <CircularProgress size={20} />}
                  {newPinAudioUrl && (
                    <Typography variant="body2" color="success.main">
                      ✓ Dosya yüklendi
                    </Typography>
                  )}
                </Box>
              )}
            </FormControl>

            <FormControl fullWidth margin="dense">
              <Autocomplete
                options={availableUsers}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                value={selectedPinOwner}
                onChange={(_, value) => setSelectedPinOwner(value)}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Avatar
                      src={option.photoUrl}
                      sx={{ width: 24, height: 24 }}
                    >
                      {option.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pin Sahibi"
                    helperText="Pinin hangi kullanıcı adına oluşturulacağını seçin"
                  />
                )}
              />
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isProtected}
                  onChange={(e) => setIsProtected(e.target.checked)}
                />
              }
              label="Pin Korumalı mı?"
              sx={{ mt: 1 }}
            />
            {isProtected && (
              <TextField
                margin="dense"
                id="pinCode"
                label="Pin Kodu (Eğer Korumalıysa)"
                type="text"
                fullWidth
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog}>İptal</Button>
            <Button onClick={handleAddNewPin}>
              {isEditMode ? "Güncelle" : "Ekle"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Edit Modal (Corrected) */}
        <Dialog
          open={openBulkEditModal}
          onClose={handleCloseBulkEditModal}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Seçili Pinleri Toplu Düzenle</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Aşağıdaki alanları değiştirerek seçili {selected.length} pini
              güncelleyebilirsiniz. "Değiştirme" seçeneği o alanı olduğu gibi
              bırakır.
            </DialogContentText>
            <FormControl fullWidth margin="dense">
              <InputLabel>Durum</InputLabel>
              <Select
                name="status"
                value={bulkEditValues.status}
                onChange={handleBulkEditChange}
                label="Durum"
              >
                <MenuItem value="nochange">Değiştirme</MenuItem>
                <MenuItem value="Aktif">Aktif</MenuItem>
                <MenuItem value="Pasif">Pasif</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel>Koruma Durumu</InputLabel>
              <Select
                name="isProtected"
                value={bulkEditValues.isProtected}
                onChange={handleBulkEditChange}
                label="Koruma Durumu"
              >
                <MenuItem value="nochange">Değiştirme</MenuItem>
                <MenuItem value="true">Korumalı</MenuItem>
                <MenuItem value="false">Korumasız</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel>Pin Seviyesi</InputLabel>
              <Select
                name="pinTier"
                value={bulkEditValues.pinTier}
                onChange={handleBulkEditChange}
                label="Pin Seviyesi"
              >
                <MenuItem value="nochange">Değiştirme</MenuItem>
                {pinTierOptions.map((tier) => (
                  <MenuItem key={tier} value={tier}>
                    {tier}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulkEditModal}>İptal</Button>
            <Button
              onClick={handleApplyBulkEdit}
              color="primary"
              variant="contained"
            >
              Uygula
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog
          open={openBulkDeleteDialog}
          onClose={handleCloseBulkDeleteDialog}
        >
          <DialogTitle>Seçili Pinleri Toplu Silme Onayı</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Seçili {selected.length} pini silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulkDeleteDialog}>İptal</Button>
            <Button onClick={handleApplyBulkDelete} color="error">
              Evet, Sil
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Pin Silme Onayı</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Bu pini silmek istediğinizden emin misiniz? Bu işlem geri
              alınamaz.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>İptal</Button>
            <Button onClick={handleDeletePin} color="error">
              Sil
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default PinManagementPage;
